/**
 * Synchronous raw DEFLATE (RFC 1951) codec, tuned for small JSON payloads.
 *
 * Exports `DeflateRaw` and `InflateRaw`. They are inverses but share no logic —
 * a bit writer against a bit reader, tree building against decode tables — so
 * what they actually share is the static table block below.
 *
 * Both ends are interoperable with the native codec: `DeflateRaw` output is read
 * by `DecompressionStream("deflate-raw")` and Python's
 * `zlib.decompressobj(wbits=-15)`, and `InflateRaw` reads what
 * `CompressionStream("deflate-raw")` and `zlib.compressobj(wbits=-15)` produce.
 * The reason to prefer these over the stream APIs is that they run
 * synchronously, which `CompressionStream` cannot — which is what reading and
 * writing compressed values in a URL needs.
 *
 * Use `CompressStream`/`UncompressStream` for bulk data or gzip/deflate wrappers.
 */

// ---- RFC 1951 static tables ----

import { Ceil, Log2, Max, Min, POS_INFINITY } from "@pdfking/math"
const LENGTH_BASE = [
  3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131,
  163, 195, 227, 258,
]
const LENGTH_EXTRA = [
  0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0,
]
const DISTANCE_BASE = [
  1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049,
  3073, 4097, 6145, 8193, 12289, 16385, 24577,
]
const DISTANCE_EXTRA = [
  0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13,
]
/** Order the code-length alphabet is transmitted in. */
const CODE_LENGTH_ORDER = [16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15]

const MAX_BITS = 15
const MAX_CODE_LENGTH_BITS = 7
const MIN_MATCH = 3
const MAX_MATCH = 258
const WINDOW = 32768
const STORED_MAX = 65535
const END_OF_BLOCK = 256
const LITERAL_SYMBOLS = 288
const DISTANCE_SYMBOLS = 30

/** match length (3..258) -> index into LENGTH_BASE */
const LENGTH_SYMBOL = new Uint8Array(MAX_MATCH + 1)
for (let i = 0; i < LENGTH_BASE.length; i++) {
  const base = LENGTH_BASE[i]
  const max = i === LENGTH_BASE.length - 1 ? MAX_MATCH : base + (1 << LENGTH_EXTRA[i]) - 1
  for (let l = base; l <= max && l <= MAX_MATCH; l++) LENGTH_SYMBOL[l] = i
}

/** distance (1..32768) -> index into DISTANCE_BASE */
const DISTANCE_SYMBOL = new Uint8Array(WINDOW + 1)
for (let i = 0; i < DISTANCE_BASE.length; i++) {
  const base = DISTANCE_BASE[i]
  const max = base + (1 << DISTANCE_EXTRA[i]) - 1
  for (let d = base; d <= max && d <= WINDOW; d++) DISTANCE_SYMBOL[d] = i
}

const FIXED_LITERAL_LENGTHS = new Uint8Array(LITERAL_SYMBOLS)
for (let i = 0; i < LITERAL_SYMBOLS; i++)
  FIXED_LITERAL_LENGTHS[i] = i < 144 ? 8 : i < 256 ? 9 : i < 280 ? 7 : 8
const FIXED_DISTANCE_LENGTHS = new Uint8Array(DISTANCE_SYMBOLS).fill(5)

// ---- token stream ----

/**
 * Flat token stream: `length[i] === 0` marks a literal whose byte is in
 * `value[i]`; otherwise it is a match of `length[i]` bytes at distance
 * `value[i]`. Kept as typed arrays rather than a union of objects because the
 * parse rebuilds the whole stream on every round.
 */
type Tokens = {
  length: Int32Array
  value: Int32Array
}

type DynamicHeader = {
  codeLengths: Uint8Array
  hclen: number
  hdist: number
  hlit: number
  sequence: ReadonlyArray<[symbol: number, extraValue: number, extraBits: number]>
}

type Candidate = {
  bits: number
  distLengths: Uint8Array
  header: DynamicHeader | undefined
  litLengths: Uint8Array
  tokens: Tokens
}

// ---- byte buffer ----

const ByteBuffer = (initial: number) => {
  let bytes = new Uint8Array(initial)
  let length = 0

  return {
    at: (i: number): number => bytes[i],
    done: (): Uint8Array => bytes.slice(0, length),
    get length(): number {
      return length
    },
    push(byte: number): void {
      if (length === bytes.length) {
        const grown = new Uint8Array(length * 2)
        grown.set(bytes)
        bytes = grown
      }
      bytes[length++] = byte
    },
  }
}

// ---- bit writer ----

const BitWriter = () => {
  const bytes = ByteBuffer(1024)
  const push = bytes.push
  let buffer = 0
  let count = 0

  return {
    align(): void {
      if (count > 0) {
        push(buffer & 0xff)
        buffer = 0
        count = 0
      }
    },
    bits(value: number, size: number): void {
      buffer |= value << count
      count += size
      while (count >= 8) {
        push(buffer & 0xff)
        buffer >>>= 8
        count -= 8
      }
    },
    done(): Uint8Array {
      if (count > 0) push(buffer & 0xff)
      return bytes.done()
    },
    raw(chunk: Uint8Array | number[]): void {
      for (const byte of chunk) push(byte)
    },
  }
}

// ---- huffman ----

/** Smallest code length that can address `used` distinct symbols. */
const FeasibleBits = (used: number): number => Max(1, Ceil(Log2(Max(2, used))))

type Node = {
  freq: number
  left: Node | undefined
  right: Node | undefined
  symbol: number
}

const BuildLengthsOnce = (freqs: Int32Array, size: number): Uint8Array => {
  const lengths = new Uint8Array(size)
  const heap: Node[] = []
  for (let i = 0; i < size; i++)
    if (freqs[i] > 0) heap.push({ freq: freqs[i], left: undefined, right: undefined, symbol: i })
  if (heap.length === 0) return lengths
  if (heap.length === 1) {
    lengths[heap[0].symbol] = 1
    return lengths
  }

  heap.sort((a, b) => a.freq - b.freq || a.symbol - b.symbol)
  while (heap.length > 1) {
    const a = heap.shift()!
    const b = heap.shift()!
    const merged: Node = { freq: a.freq + b.freq, left: a, right: b, symbol: -1 }
    let lo = 0
    let hi = heap.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (heap[mid].freq <= merged.freq) lo = mid + 1
      else hi = mid
    }
    heap.splice(lo, 0, merged)
  }

  const stack: Array<[Node, number]> = [[heap[0], 0]]
  while (stack.length > 0) {
    const [node, depth] = stack.pop()!
    if (node.symbol >= 0) lengths[node.symbol] = depth
    else {
      stack.push([node.left!, depth + 1])
      stack.push([node.right!, depth + 1])
    }
  }
  return lengths
}

/**
 * Code lengths for `freqs`, none longer than `maxBits`.
 *
 * Overlong trees are fixed by halving frequencies and rebuilding, which
 * terminates because equal frequencies bottom out at ceil(log2(symbols)) —
 * hence the cap is raised to that floor when the caller asks for less.
 */
const BuildHuffmanLengths = (freqs: Int32Array, maxBits: number): Uint8Array => {
  const size = freqs.length
  let used = 0
  for (let i = 0; i < size; i++) if (freqs[i] > 0) used++
  const limit = Max(maxBits, FeasibleBits(used))

  const scaled = Int32Array.from(freqs)
  for (;;) {
    const lengths = BuildLengthsOnce(scaled, size)
    let longest = 0
    for (const l of lengths) if (l > longest) longest = l
    if (longest <= limit) return lengths
    for (let i = 0; i < size; i++) if (scaled[i] > 0) scaled[i] = Max(1, scaled[i] >> 1)
  }
}

/**
 * Canonical codes for `lengths`, already bit-reversed: Huffman codes are
 * defined MSB-first but the bit stream is LSB-first, and codes are only ever
 * emitted, so the reversal happens once here instead of once per token.
 */
const BuildHuffmanCodes = (lengths: Uint8Array, maxBits: number): Int32Array => {
  const perLength = new Int32Array(maxBits + 1)
  for (const l of lengths) if (l > 0) perLength[l]++
  const next = new Int32Array(maxBits + 2)
  let code = 0
  for (let bits = 1; bits <= maxBits; bits++) {
    code = (code + perLength[bits - 1]) << 1
    next[bits] = code
  }
  const codes = new Int32Array(lengths.length)
  for (let i = 0; i < lengths.length; i++) {
    const l = lengths[i]
    if (l > 0) codes[i] = ReverseBits(next[l]++, l)
  }
  return codes
}

const ReverseBits = (value: number, size: number): number => {
  let out = 0
  for (let i = 0; i < size; i++) out = (out << 1) | ((value >>> i) & 1)
  return out
}

/** A one-code tree is incomplete and rejected by some inflaters; force two. */
const EnsureTwoCodes = (lengths: Uint8Array): Uint8Array => {
  let used = 0
  for (const l of lengths) if (l > 0) used++
  if (used >= 2) return lengths

  const out = Uint8Array.from(lengths)
  if (used === 0) {
    out[0] = 1
    out[1] = 1
    return out
  }
  let first = 0
  for (let i = 0; i < out.length; i++)
    if (out[i] > 0) {
      first = i
      break
    }
  out[first] = 1
  out[first === 0 ? 1 : 0] = 1
  return out
}

// ---- match finding ----

const HASH_BITS = 15
const HASH_SIZE = 1 << HASH_BITS

/**
 * Per-position match candidates as runs: step `j` of position `i` (the slice
 * `offsets[i]..offsets[i+1]` of the flat arrays) says lengths up to
 * `lengths[j]` — from one past the previous step's length, or MIN_MATCH for
 * the first — are reachable at `distances[j]`. Steps walk candidates nearest
 * first, so each length's distance is also the cheapest to encode.
 */
type MatchTable = {
  distances: Int32Array
  lengths: Int32Array
  offsets: Int32Array
}

/**
 * Hash-chain match search over the whole input. Matches depend only on the
 * data, not on the parse, so this runs once and every parse round replays it.
 */
const FindMatches = (data: Uint8Array, chainLimit: number): MatchTable => {
  const size = data.length
  const head = new Int32Array(HASH_SIZE).fill(-1)
  const prev = new Int32Array(size).fill(-1)
  const hash = (i: number): number =>
    (((data[i] << 10) ^ (data[i + 1] << 5) ^ data[i + 2]) * 0x9e3779b1) >>> (32 - HASH_BITS)

  const offsets = new Int32Array(size + 1)
  const lengths: number[] = []
  const distances: number[] = []

  for (let i = 0; i < size; i++) {
    const maxLength = Min(MAX_MATCH, size - i)
    if (maxLength >= MIN_MATCH) {
      let best = MIN_MATCH - 1
      let candidate = head[hash(i)]
      let chain = chainLimit
      while (candidate >= 0 && chain-- > 0) {
        const distance = i - candidate
        if (distance > WINDOW) break
        // Beating `best` requires matching at index `best`, so probe it first.
        if (data[candidate + best] !== data[i + best]) {
          candidate = prev[candidate]
          continue
        }
        let l = 0
        while (l < maxLength && data[candidate + l] === data[i + l]) l++
        if (l > best) {
          lengths.push(l)
          distances.push(distance)
          best = l
          if (l >= maxLength) break
        }
        candidate = prev[candidate]
      }
    }
    if (i + 2 < size) {
      const h = hash(i)
      prev[i] = head[h]
      head[h] = i
    }
    offsets[i + 1] = lengths.length
  }
  return {
    distances: Int32Array.from(distances),
    lengths: Int32Array.from(lengths),
    offsets,
  }
}

// ---- parse ----

const UNREACHABLE = 1 << 28

/**
 * Shortest path over the input where each edge is a literal or a match priced
 * with the current Huffman bit costs. Unlike greedy/lazy matching this can take
 * a short match (or a literal) now to enable a much cheaper encoding later.
 */
const OptimalParse = (
  data: Uint8Array,
  matches: MatchTable,
  litCost: Int32Array,
  distCost: Int32Array
): Tokens => {
  const size = data.length
  const cost = new Int32Array(size + 1).fill(UNREACHABLE)
  const bestLength = new Int32Array(size + 1)
  const bestValue = new Int32Array(size + 1)
  cost[0] = 0

  const lengthCost = new Int32Array(MAX_MATCH + 1)
  for (let l = MIN_MATCH; l <= MAX_MATCH; l++) {
    const ls = LENGTH_SYMBOL[l]
    lengthCost[l] = litCost[257 + ls] + LENGTH_EXTRA[ls]
  }

  for (let i = 0; i < size; i++) {
    const base = cost[i]

    const literal = base + litCost[data[i]]
    if (literal < cost[i + 1]) {
      cost[i + 1] = literal
      bestLength[i + 1] = 0
      bestValue[i + 1] = data[i]
    }

    let from = MIN_MATCH
    for (let j = matches.offsets[i]; j < matches.offsets[i + 1]; j++) {
      const distance = matches.distances[j]
      const to = matches.lengths[j]
      const ds = DISTANCE_SYMBOL[distance]
      const stepBase = base + distCost[ds] + DISTANCE_EXTRA[ds]
      for (let l = from; l <= to; l++) {
        const total = stepBase + lengthCost[l]
        if (total < cost[i + l]) {
          cost[i + l] = total
          bestLength[i + l] = l
          bestValue[i + l] = distance
        }
      }
      from = to + 1
    }
  }

  let count = 0
  for (let at = size; at > 0; count++) at -= bestLength[at] || 1
  const length = new Int32Array(count)
  const value = new Int32Array(count)
  let at = size
  for (let j = count - 1; j >= 0; j--) {
    const l = bestLength[at]
    length[j] = l
    value[j] = bestValue[at]
    at -= l === 0 ? 1 : l
  }
  return { length, value }
}

/** Single walker behind the histogram and the emit pass. */
const WalkTokens = (
  tokens: Tokens,
  literal: (byte: number) => void,
  match: (lengthSymbol: number, length: number, distanceSymbol: number, distance: number) => void
): void => {
  for (let i = 0; i < tokens.length.length; i++) {
    const l = tokens.length[i]
    if (l === 0) literal(tokens.value[i])
    else match(LENGTH_SYMBOL[l], l, DISTANCE_SYMBOL[tokens.value[i]], tokens.value[i])
  }
}

const Histogram = (tokens: Tokens): { dist: Int32Array; lit: Int32Array } => {
  const lit = new Int32Array(LITERAL_SYMBOLS)
  const dist = new Int32Array(DISTANCE_SYMBOLS)
  WalkTokens(
    tokens,
    byte => lit[byte]++,
    (ls, _l, ds) => {
      lit[257 + ls]++
      dist[ds]++
    }
  )
  lit[END_OF_BLOCK]++
  return { dist, lit }
}

/** Unused symbols get the worst price so the parse avoids introducing them. */
const CostsFromLengths = (
  litLengths: Uint8Array,
  distLengths: Uint8Array
): { distCost: Int32Array; litCost: Int32Array } => {
  const litCost = new Int32Array(LITERAL_SYMBOLS)
  const distCost = new Int32Array(DISTANCE_SYMBOLS)
  for (let i = 0; i < LITERAL_SYMBOLS; i++) litCost[i] = litLengths[i] || MAX_BITS
  for (let i = 0; i < DISTANCE_SYMBOLS; i++) distCost[i] = distLengths[i] || MAX_BITS
  return { distCost, litCost }
}

// ---- sizing / header ----

const CodeLengthSequence = (
  litLengths: Uint8Array,
  distLengths: Uint8Array
): Pick<DynamicHeader, "hdist" | "hlit" | "sequence"> => {
  let hlit = 286
  while (hlit > 257 && litLengths[hlit - 1] === 0) hlit--
  let hdist = DISTANCE_SYMBOLS
  while (hdist > 1 && distLengths[hdist - 1] === 0) hdist--

  const all = new Uint8Array(hlit + hdist)
  all.set(litLengths.subarray(0, hlit), 0)
  all.set(distLengths.subarray(0, hdist), hlit)

  const sequence: Array<[number, number, number]> = []
  let i = 0
  while (i < all.length) {
    const value = all[i]
    let run = 1
    while (i + run < all.length && all[i + run] === value) run++

    if (value === 0) {
      while (run >= 11) {
        const take = Min(138, run)
        sequence.push([18, take - 11, 7])
        run -= take
        i += take
      }
      while (run >= 3) {
        const take = Min(10, run)
        sequence.push([17, take - 3, 3])
        run -= take
        i += take
      }
      while (run-- > 0) {
        sequence.push([0, 0, 0])
        i++
      }
    } else {
      sequence.push([value, 0, 0])
      run--
      i++
      while (run >= 3) {
        const take = Min(6, run)
        sequence.push([16, take - 3, 2])
        run -= take
        i += take
      }
      while (run-- > 0) {
        sequence.push([value, 0, 0])
        i++
      }
    }
  }
  return { hdist, hlit, sequence }
}

/** Extra (non-Huffman) bits implied by the histogram; the same for every tree. */
const ExtraBits = (lit: Int32Array, dist: Int32Array): number => {
  let bits = 0
  for (let i = 0; i < LENGTH_EXTRA.length; i++) bits += lit[257 + i] * LENGTH_EXTRA[i]
  for (let i = 0; i < DISTANCE_SYMBOLS; i++) bits += dist[i] * DISTANCE_EXTRA[i]
  return bits
}

/**
 * Huffman bits for the token data (EOB included via the histogram), priced from
 * the histogram so trying a tree does not re-walk the tokens.
 */
const HistogramBits = (
  lit: Int32Array,
  dist: Int32Array,
  litLengths: Uint8Array,
  distLengths: Uint8Array
): number => {
  let bits = 0
  for (let i = 0; i < LITERAL_SYMBOLS; i++) bits += lit[i] * litLengths[i]
  for (let i = 0; i < DISTANCE_SYMBOLS; i++) bits += dist[i] * distLengths[i]
  return bits
}

const DynamicBlock = (
  lit: Int32Array,
  dist: Int32Array,
  litLengths: Uint8Array,
  distLengths: Uint8Array
): { bits: number; header: DynamicHeader } => {
  const { hdist, hlit, sequence } = CodeLengthSequence(litLengths, distLengths)

  const freqs = new Int32Array(19)
  for (const [symbol] of sequence) freqs[symbol]++
  const codeLengths = BuildHuffmanLengths(freqs, MAX_CODE_LENGTH_BITS)

  let hclen = 19
  while (hclen > 4 && codeLengths[CODE_LENGTH_ORDER[hclen - 1]] === 0) hclen--

  let bits = 17 + 3 * hclen // 3 + 5 + 5 + 4 + hclen * 3
  for (const [symbol, , extraBits] of sequence) bits += codeLengths[symbol] + extraBits
  bits += HistogramBits(lit, dist, litLengths, distLengths)

  return { bits, header: { codeLengths, hclen, hdist, hlit, sequence } }
}

// ---- emit ----

const EmitHuffmanBlock = (
  writer: ReturnType<typeof BitWriter>,
  candidate: Candidate,
  final: boolean
): void => {
  const { distLengths, header, litLengths, tokens } = candidate
  const litCodes = BuildHuffmanCodes(litLengths, MAX_BITS)
  const distCodes = BuildHuffmanCodes(distLengths, MAX_BITS)

  writer.bits(final ? 1 : 0, 1)
  writer.bits(header ? 2 : 1, 2)

  if (header) {
    writer.bits(header.hlit - 257, 5)
    writer.bits(header.hdist - 1, 5)
    writer.bits(header.hclen - 4, 4)
    for (let i = 0; i < header.hclen; i++) writer.bits(header.codeLengths[CODE_LENGTH_ORDER[i]], 3)

    const codes = BuildHuffmanCodes(header.codeLengths, MAX_CODE_LENGTH_BITS)
    for (const [symbol, extraValue, extraBits] of header.sequence) {
      writer.bits(codes[symbol], header.codeLengths[symbol])
      if (extraBits > 0) writer.bits(extraValue, extraBits)
    }
  }

  WalkTokens(
    tokens,
    byte => writer.bits(litCodes[byte], litLengths[byte]),
    (ls, l, ds, distance) => {
      writer.bits(litCodes[257 + ls], litLengths[257 + ls])
      if (LENGTH_EXTRA[ls] > 0) writer.bits(l - LENGTH_BASE[ls], LENGTH_EXTRA[ls])
      writer.bits(distCodes[ds], distLengths[ds])
      if (DISTANCE_EXTRA[ds] > 0) writer.bits(distance - DISTANCE_BASE[ds], DISTANCE_EXTRA[ds])
    }
  )

  writer.bits(litCodes[END_OF_BLOCK], litLengths[END_OF_BLOCK])
}

/** Stored blocks cap at 65535 bytes, so long input becomes several blocks. */
const EmitStored = (writer: ReturnType<typeof BitWriter>, data: Uint8Array): void => {
  const size = data.length
  let offset = 0
  do {
    const take = Min(STORED_MAX, size - offset)
    const final = offset + take >= size
    writer.bits(final ? 1 : 0, 1)
    writer.bits(0, 2)
    writer.align()
    writer.raw([take & 0xff, (take >>> 8) & 0xff, ~take & 0xff, (~take >>> 8) & 0xff])
    writer.raw(data.subarray(offset, offset + take))
    offset += take
  } while (offset < size)
}

const StoredBits = (size: number): number => {
  const blocks = Max(1, Ceil(size / STORED_MAX))
  // 3 header bits + up to 7 padding bits + 32 bits of LEN/NLEN per block
  return 42 * blocks + 8 * size // blocks * (3 + 7 + 32) + size * 8
}

// ---- public: encode ----

/**
 * Compresses bytes to a raw deflate stream (no zlib or gzip wrapper).
 *
 * Compresses *better* than zlib level 9 on JSON-shaped input (~1-12% smaller)
 * because it does not use zlib's greedy/lazy match heuristic. Instead it runs a
 * zopfli-style loop: pick the token stream by shortest path over the real
 * Huffman bit costs, rebuild the trees from that stream, then re-run the parse
 * with the updated costs until it stops improving. On short payloads the tree
 * description is ~20% of the output, so it also tries capping the maximum code
 * length — a cheaper-to-describe tree can beat the entropy-optimal one.
 *
 * Cost grows faster than linearly with input size; it is aimed at
 * URL/querystring-sized documents (a few ms for <1KB, ~100ms for 10KB).
 *
 * @param data - Bytes to compress
 * @param rounds - Max parse/retree iterations; lower is faster and slightly larger
 * @returns Raw deflate bytes
 *
 * @example
 * ```ts
 * import { Base64URLEncode, DeflateRaw, JSONEncode, StringToUInt8 } from "@pdfking/tfunc"
 *
 * const token = Base64URLEncode(DeflateRaw(StringToUInt8(JSONEncode(imposition_data))))
 * ```
 */
export const DeflateRaw = (data: Uint8Array, rounds = 8): Uint8Array => {
  const size = data.length

  if (size === 0) {
    const writer = BitWriter()
    EmitHuffmanBlock(
      writer,
      {
        bits: 0,
        distLengths: FIXED_DISTANCE_LENGTHS,
        header: undefined,
        litLengths: FIXED_LITERAL_LENGTHS,
        tokens: { length: new Int32Array(0), value: new Int32Array(0) },
      },
      true
    )
    return writer.done()
  }

  const matches = FindMatches(data, size <= 8192 ? 4096 : 256)
  let litCost: Int32Array<ArrayBufferLike> = Int32Array.from(FIXED_LITERAL_LENGTHS)
  let distCost: Int32Array<ArrayBufferLike> = Int32Array.from(FIXED_DISTANCE_LENGTHS)

  let best: Candidate | undefined
  const consider = (candidate: Candidate) => {
    if (!best || candidate.bits < best.bits) best = candidate
  }

  let previous = POS_INFINITY
  for (let round = 0; round < rounds; round++) {
    const tokens = OptimalParse(data, matches, litCost, distCost)
    const { dist, lit } = Histogram(tokens)
    const extra = ExtraBits(lit, dist)
    const distLengths = EnsureTwoCodes(BuildHuffmanLengths(dist, MAX_BITS))

    let roundBest: Candidate | undefined
    const dynamic = (litLengths: Uint8Array) => {
      const { bits, header } = DynamicBlock(lit, dist, litLengths, distLengths)
      const candidate: Candidate = { bits: bits + extra, distLengths, header, litLengths, tokens }
      consider(candidate)
      if (!roundBest || candidate.bits < roundBest.bits) roundBest = candidate
    }

    // On short payloads the tree description is a large share of the output, so
    // a shallower (cheaper to describe) tree can win overall. Caps at or above
    // the entropy-optimal depth all produce that same tree, so only caps below
    // it are worth trying.
    const entropy = EnsureTwoCodes(BuildHuffmanLengths(lit, MAX_BITS))
    let longest = 0
    for (const l of entropy) if (l > longest) longest = l
    dynamic(entropy)
    const floor = FeasibleBits(lit.reduce((n, f) => n + (f > 0 ? 1 : 0), 0))
    for (let cap = longest - 1; cap >= floor; cap--)
      dynamic(EnsureTwoCodes(BuildHuffmanLengths(lit, cap)))

    consider({
      bits: 3 + HistogramBits(lit, dist, FIXED_LITERAL_LENGTHS, FIXED_DISTANCE_LENGTHS) + extra,
      distLengths: FIXED_DISTANCE_LENGTHS,
      header: undefined,
      litLengths: FIXED_LITERAL_LENGTHS,
      tokens,
    })

    if (roundBest!.bits >= previous) break
    previous = roundBest!.bits
    const next = CostsFromLengths(roundBest!.litLengths, roundBest!.distLengths)
    litCost = next.litCost
    distCost = next.distCost
  }

  const writer = BitWriter()
  if (StoredBits(size) < best!.bits) EmitStored(writer, data)
  else EmitHuffmanBlock(writer, best!, true)
  return writer.done()
}

// ---- decode ----

const BitReader = (data: Uint8Array) => {
  let position = 0
  let buffer = 0
  let count = 0

  return {
    /** Discards the rest of the current byte. */
    align(): void {
      buffer = 0
      count = 0
    },
    bits(size: number): number {
      while (count < size) {
        if (position >= data.length) throw new Error("Unexpected end of deflate stream")
        buffer |= data[position++] << count
        count += 8
      }
      const value = buffer & ((1 << size) - 1)
      buffer >>>= size
      count -= size
      return value
    },
  }
}

/**
 * Canonical Huffman decode table: `counts[n]` codes are n bits long, and
 * `symbols` lists every coded symbol ordered by code length then symbol. That is
 * enough to walk the code space a bit at a time without materialising a tree.
 */
type Decoder = {
  counts: Int32Array
  symbols: Int32Array
}

const BuildDecoder = (lengths: Uint8Array): Decoder => {
  const counts = new Int32Array(MAX_BITS + 1)
  for (const l of lengths) counts[l]++
  counts[0] = 0

  const offsets = new Int32Array(MAX_BITS + 2)
  for (let bits = 1; bits <= MAX_BITS; bits++) offsets[bits + 1] = offsets[bits] + counts[bits]

  const symbols = new Int32Array(lengths.length)
  for (let symbol = 0; symbol < lengths.length; symbol++) {
    const l = lengths[symbol]
    if (l > 0) symbols[offsets[l]++] = symbol
  }
  return { counts, symbols }
}

const DecodeSymbol = (reader: ReturnType<typeof BitReader>, decoder: Decoder): number => {
  let code = 0
  let first = 0
  let index = 0
  for (let length = 1; length <= MAX_BITS; length++) {
    code |= reader.bits(1)
    const count = decoder.counts[length]
    if (code - first < count) return decoder.symbols[index + (code - first)]
    index += count
    first = (first + count) << 1
    code <<= 1
  }
  throw new Error("Invalid Huffman code in deflate stream")
}

const FIXED_LITERAL_DECODER = BuildDecoder(FIXED_LITERAL_LENGTHS)
const FIXED_DISTANCE_DECODER = BuildDecoder(FIXED_DISTANCE_LENGTHS)

/** Reads the per-block Huffman trees that a dynamic block carries inline. */
const ReadDynamicTrees = (
  reader: ReturnType<typeof BitReader>
): { dist: Decoder; lit: Decoder } => {
  const hlit = reader.bits(5) + 257
  const hdist = reader.bits(5) + 1
  const hclen = reader.bits(4) + 4

  const codeLengths = new Uint8Array(19)
  for (let i = 0; i < hclen; i++) codeLengths[CODE_LENGTH_ORDER[i]] = reader.bits(3)
  const codeDecoder = BuildDecoder(codeLengths)

  const lengths = new Uint8Array(hlit + hdist)
  let i = 0
  while (i < lengths.length) {
    const symbol = DecodeSymbol(reader, codeDecoder)
    if (symbol < 16) {
      lengths[i++] = symbol
      continue
    }
    let repeat: number
    let value = 0
    if (symbol === 16) {
      if (i === 0) throw new Error("Deflate code length repeat with no previous length")
      value = lengths[i - 1]
      repeat = 3 + reader.bits(2)
    } else if (symbol === 17) repeat = 3 + reader.bits(3)
    else repeat = 11 + reader.bits(7)

    if (i + repeat > lengths.length) throw new Error("Deflate code length repeat overruns tree")
    while (repeat-- > 0) lengths[i++] = value
  }

  return {
    dist: BuildDecoder(lengths.subarray(hlit)),
    lit: BuildDecoder(lengths.subarray(0, hlit)),
  }
}

// ---- public: decode ----

/**
 * Decompresses a raw deflate stream (no zlib or gzip wrapper).
 *
 * Reads all three block types, so it handles output from `DeflateRaw`,
 * `CompressionStream("deflate-raw")` and `zlib.compressobj(wbits=-15)` alike.
 * Throws on a malformed or truncated stream rather than returning partial data.
 *
 * @param data - Raw deflate bytes
 * @returns The decompressed bytes
 *
 * @example
 * ```ts
 * import { Base64URLDecode, InflateRaw, JSONDecode, UInt8ToString } from "@pdfking/tfunc"
 *
 * const imposition_data = JSONDecode(UInt8ToString(InflateRaw(Base64URLDecode(token))))
 * ```
 */
export const InflateRaw = (data: Uint8Array): Uint8Array => {
  const reader = BitReader(data)
  const out = ByteBuffer(Max(64, data.length * 4))
  const push = out.push

  for (;;) {
    const final = reader.bits(1)
    const type = reader.bits(2)

    if (type === 0) {
      reader.align()
      const size = reader.bits(16)
      const check = reader.bits(16)
      if ((size ^ 0xffff) !== check)
        throw new Error("Corrupt stored block length in deflate stream")
      for (let i = 0; i < size; i++) push(reader.bits(8))
    } else if (type === 1 || type === 2) {
      const { dist, lit } =
        type === 1
          ? { dist: FIXED_DISTANCE_DECODER, lit: FIXED_LITERAL_DECODER }
          : ReadDynamicTrees(reader)

      for (;;) {
        const symbol = DecodeSymbol(reader, lit)
        if (symbol === END_OF_BLOCK) break
        if (symbol < END_OF_BLOCK) {
          push(symbol)
          continue
        }

        const lengthIndex = symbol - 257
        if (lengthIndex >= LENGTH_BASE.length)
          throw new Error(`Invalid deflate length symbol: ${symbol}`)
        const matchLength = LENGTH_BASE[lengthIndex] + reader.bits(LENGTH_EXTRA[lengthIndex])

        const distanceSymbol = DecodeSymbol(reader, dist)
        if (distanceSymbol >= DISTANCE_BASE.length)
          throw new Error(`Invalid deflate distance symbol: ${distanceSymbol}`)
        const distance = DISTANCE_BASE[distanceSymbol] + reader.bits(DISTANCE_EXTRA[distanceSymbol])
        if (distance > out.length)
          throw new Error("Deflate match distance precedes start of output")

        for (let i = 0; i < matchLength; i++) push(out.at(out.length - distance))
      }
    } else throw new Error("Invalid deflate block type")

    if (final) break
  }

  return out.done()
}
